import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";

import axiosInstance from "../axiosInstance";
import {
  createSetting,
  deleteSetting,
  listSettings,
  updateSetting,
} from "./settingsService";

const empty = {
  name: "",
  currency_code: "USD",
  handling_fee: "0.00",
  bank: "",
  is_active: true,
};

const Icon = ({
  icon,
  title,
  className = "",
  ...props
}) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    className={`settings-icon-action ${className}`}
    {...props}
  >
    <i className={`bi bi-${icon}`}></i>
  </button>
);

export default function PaymentTenders() {
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [baseCurrency, setBaseCurrency] = useState("USD");
  const [form, setForm] = useState(empty);
  const [edit, setEdit] = useState(null);
  const [previewAmount, setPreviewAmount] =
    useState("100.00");
  const [serverQuote, setServerQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);

  const load = async () => {
    const [tenders, bankList, currencyList] =
      await Promise.all([
        listSettings("payment-methods"),
        listSettings("banks"),
        listSettings("currencies"),
      ]);
    const company = await axiosInstance.get(
      "settings/company/"
    );

    setItems(tenders);
    setBanks(bankList);
    setCurrencies(currencyList);
    setBaseCurrency(
      company.data?.default_currency_code || "USD"
    );
  };

  useEffect(() => {
    load().catch(() =>
      toast.error("Could not load payment tenders.")
    );
  }, []);

  const localPreview = useMemo(() => {
    const gross = Number(previewAmount || 0);
    const feePercent = Number(form.handling_fee || 0);
    const selectedCurrency = currencies.find(
      (item) => item.currency_code === (form.currency_code || baseCurrency)
    );
    const exchangeRate = Number(selectedCurrency?.units_per_base || 0);

    const feeAmount = gross * feePercent / 100;
    const netAmount = gross - feeAmount;
    const homeAmount =
      exchangeRate > 0 ? netAmount / exchangeRate : 0;

    return {
      gross,
      feeAmount,
      netAmount,
      homeAmount,
    };
  }, [
    previewAmount,
    form.handling_fee,
    form.currency_code,
    currencies,
    baseCurrency,
  ]);

  const save = async (event) => {
    event.preventDefault();

    const handling = Number(form.handling_fee);

    if (handling < 0 || handling > 100) {
      toast.error(
        "Handling fee must be between 0 and 100 percent."
      );
      return;
    }

    try {
      const payload = { ...form };
      delete payload.exchange_rate;
      if (edit) {
        await updateSetting(
          "payment-methods",
          edit,
          payload
        );
      } else {
        await createSetting("payment-methods", payload);
      }

      setEdit(null);
      setForm(empty);
      setServerQuote(null);
      await load();
      toast.success("Payment tender saved.");
    } catch (error) {
      toast.error(
        Object.values(error.response?.data || {})
          .flat()
          .join(" ") ||
          "Could not save payment tender."
      );
    }
  };

  const testSavedTender = async (tender) => {
    try {
      setQuoting(true);

      const response = await axiosInstance.post(
        "payments/quote/",
        {
          payment_method: tender.id,
          gross_tender_amount: previewAmount || "0.00",
        }
      );

      setServerQuote(response.data);
      toast.success("Exchange-rate test completed.");
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Could not calculate the payment quote."
      );
    } finally {
      setQuoting(false);
    }
  };

  return (
    <section className="settings-panel">
      <div className="settings-panel__head">
        <div>
          <h2>Payment methods</h2>
          <p>
            Maintain payment tenders, exchange rates and
            percentage handling fees.
          </p>
        </div>
      </div>

      <div className="payment-rate-guidance">
        <i className="bi bi-info-circle"></i>
        <div>
          <strong>Exchange-rate convention</strong>
          <p>
            Enter how many tender-currency units equal one
            home-currency unit. Net tender amount is divided
            by this rate.
          </p>
        </div>
      </div>

      <form
        className="settings-form-grid"
        onSubmit={save}
      >
        <label>
          <span>Tender name</span>
          <input
            value={form.name}
            onChange={(event) =>
              setForm({
                ...form,
                name: event.target.value.toUpperCase(),
              })
            }
            required
          />
        </label>

        <label>
          <span>Tender currency</span>
          <select
            value={form.currency_code || baseCurrency}
            onChange={(event) => {
              const code = event.target.value;
              setForm({
                ...form,
                currency_code: code,
              });
            }}
          >
            {currencies
              .filter((currency) => currency.is_active)
              .map((currency) => (
                <option
                  key={currency.id}
                  value={currency.currency_code}
                >
                  {currency.currency_code} — {currency.name}
                </option>
              ))}
          </select>
        </label>

        <label>
          <span>Exchange rate</span>
          <input
            type="number"
            min="0.0001"
            step="0.0001"
            value={
              currencies.find(
                (item) => item.currency_code === (form.currency_code || baseCurrency)
              )?.units_per_base || ""
            }
            readOnly
          />
          <small>
            Live from Currencies — never maintained separately here.
            This rate updates automatically when the currency rate changes.
          </small>
        </label>

        <label>
          <span>Handling fee (%)</span>
          <input
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.handling_fee}
            onChange={(event) =>
              setForm({
                ...form,
                handling_fee: event.target.value,
              })
            }
          />
        </label>

        <label>
          <span>Bank</span>
          <select
            value={form.bank || ""}
            onChange={(event) =>
              setForm({
                ...form,
                bank: event.target.value,
              })
            }
          >
            <option value="">NONE</option>
            {banks.map((bank) => (
              <option key={bank.id} value={bank.id}>
                {bank.name}
              </option>
            ))}
          </select>
        </label>

        <div className="settings-form-actions">
          <button>
            <i className="bi bi-check2-circle"></i>
            {edit ? " Update" : " Add"}
          </button>
        </div>
      </form>

      <div className="payment-rate-preview">
        <div className="payment-rate-preview__input">
          <label>
            <span>Test tender amount</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={previewAmount}
              onChange={(event) => {
                setPreviewAmount(event.target.value);
                setServerQuote(null);
              }}
            />
          </label>
        </div>

        <div>
          <span>Gross tender</span>
          <strong>
            {localPreview.gross.toFixed(2)}
          </strong>
        </div>

        <div>
          <span>Handling fee</span>
          <strong>
            {localPreview.feeAmount.toFixed(2)}
          </strong>
        </div>

        <div>
          <span>Net tender</span>
          <strong>
            {localPreview.netAmount.toFixed(2)}
          </strong>
        </div>

        <div className="highlight">
          <span>Home-currency amount</span>
          <strong>
            {localPreview.homeAmount.toFixed(2)}
          </strong>
        </div>
      </div>

      {serverQuote && (
        <div className="payment-server-quote">
          <i className="bi bi-shield-check"></i>
          <span>
            Server verified: {serverQuote.net_tender_amount}
            {" "}÷ {serverQuote.exchange_rate}
            {" "}= {serverQuote.home_currency_amount}
          </span>
        </div>
      )}

      <div className="settings-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Tender code</th>
              <th>Tender name</th>
              <th>Currency</th>
              <th>Exchange rate</th>
              <th>Handling fee</th>
              <th>Bank</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{item.payment_code}</td>
                <td>{item.name}</td>
                <td>{item.currency_code}</td>
                <td>{
                  currencies.find((currency) => currency.currency_code === item.currency_code)
                    ?.units_per_base || item.exchange_rate
                }</td>
                <td>{item.handling_fee}%</td>
                <td>{item.bank_name || "—"}</td>
                <td>
                  <Icon
                    icon="calculator"
                    title="Test exchange rate"
                    onClick={() =>
                      testSavedTender(item)
                    }
                    disabled={quoting}
                  />

                  <Icon
                    icon="pencil-square"
                    title="Edit"
                    onClick={() => {
                      setEdit(item.id);
                      setForm({
                        ...item,
                        bank: item.bank || "",
                      });
                      setServerQuote(null);
                    }}
                  />

                  <Icon
                    icon="trash3"
                    title="Delete"
                    className="danger"
                    onClick={async () => {
                      if (
                        window.confirm(
                          "Delete this tender?"
                        )
                      ) {
                        await deleteSetting(
                          "payment-methods",
                          item.id
                        );
                        await load();
                      }
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
